import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { getBankDetails, clearBankDetails } from '../../../../redux/Slices/authSlice';
import { FaArrowRight, FaEdit } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { useTitle } from '../../../../shared/hooks/TitleContext';

const BankDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { id } = useParams();
  const { bankDetails, isLoading } = useSelector((s) => s.auth);
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    setTitle('banks.details.title');
  }, [setTitle]);

  useEffect(() => {
    if (id) {
      dispatch(getBankDetails(id));
    }
    return () => {
      dispatch(clearBankDetails());
    };
  }, [dispatch, id]);

  if (isLoading) {
    return (
      <div style={{ 
        padding: '30px', 
        backgroundColor: '#14263b', 
        minHeight: 'calc(100vh - 80px)', 
        color: 'white', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        direction: dir
      }}>
        {t('common.loading', { defaultValue: 'Loading...' })}
      </div>
    );
  }

  if (!bankDetails) {
    return (
      <div style={{ 
        padding: '30px', 
        backgroundColor: '#14263b', 
        minHeight: 'calc(100vh - 80px)', 
        color: 'white',
        direction: dir
      }}>
        <button 
          onClick={() => navigate('/banks')} 
          style={{ 
            background: '#666', 
            color: 'white', 
            border: 'none', 
            borderRadius: 8, 
            padding: '8px 12px', 
            cursor: 'pointer', 
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowRight style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} />
          {t('common.back', { defaultValue: 'Back' })}
        </button>
        <p style={{ textAlign }}>{t('banks.details.noData', { defaultValue: 'No data available' })}</p>
      </div>
    );
  }

  const bank = bankDetails.data || bankDetails;

  return (
    <div style={{ 
      padding: '30px', 
      backgroundColor: '#14263b', 
      minHeight: 'calc(100vh - 80px)', 
      color: 'white',
      direction: dir
    }}>
      <div style={{ 
        marginBottom: '20px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <button 
          onClick={() => navigate('/banks')} 
          style={{ 
            background: '#666', 
            color: 'white', 
            border: 'none', 
            borderRadius: 8, 
            padding: '8px 12px', 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaArrowRight style={{ transform: isRTL ? 'rotate(180deg)' : 'none' }} />
          {t('common.back', { defaultValue: 'Back' })}
        </button>
        <button
          onClick={() => navigate(`/banks/edit/${id}`)}
          style={{
            backgroundColor: '#B3B3B3',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexDirection: isRTL ? 'row' : 'row-reverse'
          }}
        >
          <FaEdit />
          {t('banks.details.edit', { defaultValue: 'Edit Bank' })}
        </button>
      </div>
      
      <div style={{ 
        backgroundColor: '#1b3350', 
        border: '1px solid #333', 
        borderRadius: '12px', 
        overflow: 'hidden',
        direction: dir
      }}>
        <div style={{ 
          padding: '20px', 
          borderBottom: '1px solid #333', 
          backgroundColor: '#1a1f2e'
        }}>
          <strong style={{ 
            fontSize: '18px', 
            color: 'white',
            textAlign
          }}>
            {t('banks.details.title', { defaultValue: 'Bank Details' })} #{bank.id}
          </strong>
        </div>
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <div>
              <div style={{ 
                color: '#9ca3af', 
                fontSize: '12px', 
                marginBottom: '8px',
                textAlign,
                direction: dir
              }}>
                {t('banks.details.id', { defaultValue: 'ID' })}
              </div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: 'white',
                textAlign,
                direction: dir
              }}>
                {bank.id || '—'}
              </div>
            </div>
            <div>
              <div style={{ 
                color: '#9ca3af', 
                fontSize: '12px', 
                marginBottom: '8px',
                textAlign,
                direction: dir
              }}>
                {t('banks.details.nameEn', { defaultValue: 'Name (English)' })}
              </div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: 'white',
                textAlign: 'left',
                direction: 'ltr'
              }}>
                {bank.name_en || bank.name || '—'}
              </div>
            </div>
            <div>
              <div style={{ 
                color: '#9ca3af', 
                fontSize: '12px', 
                marginBottom: '8px',
                textAlign,
                direction: dir
              }}>
                {t('banks.details.nameAr', { defaultValue: 'Name (Arabic)' })}
              </div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: 'white',
                textAlign: 'right',
                direction: 'rtl'
              }}>
                {bank.name_ar || bank.name || '—'}
              </div>
            </div>
            <div>
              <div style={{ 
                color: '#9ca3af', 
                fontSize: '12px', 
                marginBottom: '8px',
                textAlign,
                direction: dir
              }}>
                {t('banks.details.accountNumber', { defaultValue: 'Account Number' })}
              </div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: 'white',
                textAlign,
                direction: dir
              }}>
                {bank.account_number || '—'}
              </div>
            </div>
            <div>
              <div style={{ 
                color: '#9ca3af', 
                fontSize: '12px', 
                marginBottom: '8px',
                textAlign,
                direction: dir
              }}>
                {t('banks.details.iban', { defaultValue: 'IBAN' })}
              </div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: 'white',
                textAlign,
                direction: dir
              }}>
                {bank.iban || '—'}
              </div>
            </div>
            <div>
              <div style={{ 
                color: '#9ca3af', 
                fontSize: '12px', 
                marginBottom: '8px',
                textAlign,
                direction: dir
              }}>
                {t('banks.details.swiftCode', { defaultValue: 'SWIFT Code' })}
              </div>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 'bold', 
                color: 'white',
                textAlign,
                direction: dir
              }}>
                {bank.swift_code || '—'}
              </div>
            </div>
            <div>
              <div style={{ 
                color: '#9ca3af', 
                fontSize: '12px', 
                marginBottom: '8px',
                textAlign,
                direction: dir
              }}>
                {t('banks.details.status', { defaultValue: 'Status' })}
              </div>
              <span style={{
                backgroundColor: bank.is_active ? '#AC2000' : '#dc3545',
                color: 'white',
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 'bold',
                display: 'inline-block'
              }}>
                {bank.is_active ? t('banks.status.active', { defaultValue: 'Active' }) : t('banks.status.inactive', { defaultValue: 'Inactive' })}
              </span>
            </div>
            {bank.notes && (
              <div style={{ gridColumn: '1 / span 2' }}>
                <div style={{ 
                  color: '#9ca3af', 
                  fontSize: '12px', 
                  marginBottom: '8px',
                  textAlign,
                  direction: dir
                }}>
                  {t('banks.details.notes', { defaultValue: 'Notes' })}
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                  color: 'white',
                  textAlign,
                  direction: dir
                }}>
                  {bank.notes}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankDetails;
